import { googleSignIn } from "@/lib/authGoogle";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import { getSafeReturnTo } from "@/lib/navigation/publicReturnTo";
import { router, useLocalSearchParams } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  Text,
  View,
} from "react-native";

export default function SignIn() {
  const { user, ownerReady, ownerError } = useOwnerAuth();
  const params = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const [busy, setBusy] = useState(false);
  const returnTo = useMemo(
    () => getSafeReturnTo(params.returnTo) || "/(owner)/start",
    [params.returnTo]
  );

  const routedRef = useRef(false);

  useEffect(() => {
    if (!user || !ownerReady || routedRef.current) return;
    routedRef.current = true;
    router.replace(returnTo as any);
  }, [user, ownerReady, returnTo]);

  useEffect(() => {
    if (!ownerError) return;
    Alert.alert(
      "Account setup failed",
      "You are signed in, but we could not prepare your owner account. Please try again."
    );
  }, [ownerError]);

  const handleGoogleSignIn = useCallback(async () => {
    if (busy) {
      return;
    }

    try {
      setBusy(true);

      const credential = await googleSignIn();

      if (credential && !credential.user?.uid) {
        Alert.alert(
          "Google login failed",
          "Google login completed, but no Firebase user was returned. Please try again."
        );
      }
    } catch (error: any) {
      routedRef.current = false;

      Alert.alert(
        "Google login failed",
        error?.message ??
          "Google login failed. Please check your Google Sign-In configuration."
      );
    } finally {
      setBusy(false);
    }
  }, [busy]);

  return (
    <View
      style={{
        flex: 1,
        padding: 16,
        justifyContent: "center",
        gap: 12,
        backgroundColor: "#FFFFFF",
      }}
    >
      <Text
        style={{
          fontSize: 22,
          fontWeight: "900",
          color: "#111827",
        }}
      >
        Login
      </Text>

      <Text
        style={{
          fontSize: 14,
          color: "#6B7280",
          lineHeight: 20,
          fontWeight: "600",
        }}
      >
        Continue with Google to create or manage your Soko Grid owner account.
      </Text>

      <Pressable
        disabled={busy}
        onPress={handleGoogleSignIn}
        style={{
          height: 48,
          borderRadius: 14,
          backgroundColor: busy ? "#9CA3AF" : "#111827",
          alignItems: "center",
          justifyContent: "center",
          opacity: busy ? 0.7 : 1,
          flexDirection: "row",
          gap: 8,
        }}
      >
        {busy ? <ActivityIndicator color="#FFFFFF" /> : null}

        <Text style={{ color: "#FFFFFF", fontWeight: "900" }}>
          {busy ? "Signing in..." : "Continue with Google"}
        </Text>
      </Pressable>
    </View>
  );
}
